import type { QueryClient } from '@tanstack/react-query';
import { createMutation, createQuery } from 'react-query-kit';
import type { ApiError } from '../ApiError';
import { BaseApi } from '../BaseApi';
import type { Category } from '../common';
import type { Feature } from './Feature';
import type { CreateFeatureRequest, FeatureApi, WriteDescriptionRequest, WriteDescriptionResult } from './FeatureApi';
import type { PatchFeature } from './PatchFeature';

interface RawFeature extends Omit<Feature, 'createdAt' | 'eTag' | 'updatedAt'> {
  readonly createdAt: string;
  readonly updatedAt: string;
}

export class DefaultFeatureApi extends BaseApi implements FeatureApi {
  public readonly categories = createQuery<Category[], unknown, ApiError>({
    queryKey: ['feature/categories'],
    fetcher: () => this.doGet<Category[]>('/api/feature/categories'),
  });

  public readonly create = createMutation<number, CreateFeatureRequest, ApiError>({
    mutationKey: ['feature/create'],
    mutationFn: async (request) => {
      const { id } = await this.doPost<{ readonly id: number }>('/api/feature', JSON.stringify(request));
      return id;
    },
  });

  public readonly writeDescription = createQuery<WriteDescriptionResult, WriteDescriptionRequest, ApiError>({
    queryKey: ['feature/write-description'],
    fetcher: async (request) => {
      const result = await this.doPost<Omit<WriteDescriptionResult, 'type'>>(
        `/api/feature/write-description`,
        JSON.stringify(request),
      );
      return {
        ...result,
        type: request.type,
      };
    },
  });

  public constructor(queryClient: QueryClient) {
    super(queryClient);
  }

  public readonly id = (id: number) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    delete: createMutation<void, void, ApiError>({
      mutationKey: ['feature/delete', id],
      mutationFn: (): Promise<void> => this.doDelete(`/api/feature/${id.toString()}`),
      onSettled: () => {
        void this.queryClient.invalidateQueries({
          queryKey: ['feature/get', id],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['property/features'],
        });
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    get: createQuery<Feature, void, ApiError>({
      queryKey: ['feature/get', id],
      fetcher: async (): Promise<Feature> => {
        const result = await this.doFullGet<RawFeature>(`/api/feature/${id.toString()}`);
        return {
          ...result.body,
          createdAt: new Date(result.body.createdAt),
          updatedAt: new Date(result.body.updatedAt),
          eTag: result.headers.get('ETag') ?? 'no-etag',
        };
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    patch: createMutation<void, PatchFeature, ApiError>({
      mutationKey: ['feature/patch', id],
      mutationFn: ({ eTag, fields }) => this.doPatch(`/api/feature/${id.toString()}`, eTag, JSON.stringify(fields)),
      onSettled: () => {
        void this.queryClient.invalidateQueries({
          queryKey: ['feature/get', id],
        });
      },
      onSuccess: () => {
        void this.queryClient.invalidateQueries({
          queryKey: ['feature/get', id],
        });
      },
    }),
  });
}
